import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, AlertTriangle, Building2, User } from "lucide-react";

export default function AssociatedEntities() {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    entity_type: "donor",
    description: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    restrictions: "",
    flagged_for_conflict: false,
    notes: "",
  });

  const { data: entities, isLoading } = useQuery({
    queryKey: ["associated_entities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("associated_entities" as any)
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const addEntityMutation = useMutation({
    mutationFn: async (entity: typeof formData) => {
      const { error } = await supabase
        .from("associated_entities" as any)
        .insert([entity]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["associated_entities"] });
      toast.success("Entity added successfully");
      setIsAddDialogOpen(false);
      setFormData({
        name: "",
        entity_type: "donor",
        description: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        restrictions: "",
        flagged_for_conflict: false,
        notes: "",
      });
    },
    onError: (error) => {
      toast.error("Failed to add entity: " + error.message);
    },
  });

  const getEntityIcon = (type: string) => {
    switch (type) {
      case "donor":
      case "booster":
        return <Building2 className="h-4 w-4" />;
      case "agent":
      case "third_party":
        return <User className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Associated Entities</h1>
            <p className="text-muted-foreground">
              Manage donors, boosters, agents, and third parties for conflict detection
            </p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Entity
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Associated Entity</DialogTitle>
                <DialogDescription>
                  Add a new entity to track for potential conflicts of interest
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Entity Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Entity name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="entity_type">Type *</Label>
                    <Select
                      value={formData.entity_type}
                      onValueChange={(value) => setFormData({ ...formData, entity_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="donor">Donor</SelectItem>
                        <SelectItem value="booster">Booster</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="third_party">Third Party</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the entity"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="restrictions">Restrictions/Concerns</Label>
                  <Textarea
                    id="restrictions"
                    value={formData.restrictions}
                    onChange={(e) => setFormData({ ...formData, restrictions: e.target.value })}
                    placeholder="Any specific restrictions or concerns about this entity"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="flagged"
                    checked={formData.flagged_for_conflict}
                    onCheckedChange={(checked) => setFormData({ ...formData, flagged_for_conflict: checked })}
                  />
                  <Label htmlFor="flagged" className="cursor-pointer">
                    Flag for conflict detection
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Internal Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Internal notes (not visible to athletes)"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => addEntityMutation.mutate(formData)}
                  disabled={!formData.name || addEntityMutation.isPending}
                >
                  Add Entity
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid gap-4">
            {entities?.map((entity: any) => (
              <Card key={entity.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getEntityIcon(entity.entity_type)}
                      <CardTitle>{entity.name}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">
                        {entity.entity_type.replace("_", " ")}
                      </Badge>
                      {entity.flagged_for_conflict && (
                        <Badge variant="destructive">
                          <AlertTriangle className="mr-1 h-3 w-3" />
                          Flagged
                        </Badge>
                      )}
                    </div>
                  </div>
                  {entity.description && (
                    <CardDescription>{entity.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {entity.contact_name && (
                      <div>
                        <span className="font-medium">Contact:</span> {entity.contact_name}
                      </div>
                    )}
                    {entity.contact_email && (
                      <div>
                        <span className="font-medium">Email:</span> {entity.contact_email}
                      </div>
                    )}
                    {entity.restrictions && (
                      <div className="col-span-2">
                        <span className="font-medium">Restrictions:</span>
                        <p className="text-muted-foreground mt-1">{entity.restrictions}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {entities?.length === 0 && (
              <Card>
                <CardContent className="text-center py-8 text-muted-foreground">
                  No associated entities found. Add one to get started.
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
